import {
  EDUCATION_LEVEL_OPTIONS,
  LEARNING_GOAL_OPTIONS,
  ROLE_OPTIONS_TYPE,
  SKILL_OPTIONS_TYPE,
} from 'bundles/onboarding-common/constants';
import type { LearnerObjectAttributesCurrentOccupationQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerObjectAttributesCurrentOccupation';
import type { LearnerObjectAttributesEducationProfileQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerObjectAttributesEducationProfile';
import type { LearnerObjectAttributesInterestedCareersQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerObjectAttributesInterestedCareers';
import type { LearnerObjectAttributesLearningGoalQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerObjectAttributesLearningGoal';
import type { LearnerObjectAttributesSkillsWantedQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerObjectAttributesSkillsWanted';
import type { LearnerProfileDesiredSkillsQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerProfileDesiredSkills';
import type { LearnerProfileOccupationPreferencesQueryQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerProfileOccupationPreferences';
import type { Skill } from 'bundles/onboarding-common/types';
import { slugifyCustomRoleName } from 'bundles/onboarding-common/utils';

const formatOccupationPreferencesFromLearnerProfile = (
  occupationPreferencesData?: LearnerProfileOccupationPreferencesQueryQuery
) => {
  const value =
    occupationPreferencesData?.LearnerProfile?.me?.occupationPreferences?.map((occupationPreference) => {
      if (occupationPreference.occupation.__typename === 'LearnerProfile_OccupationId') {
        return {
          id: occupationPreference.occupation.id,
          name: '',
          type: ROLE_OPTIONS_TYPE.DEFAULT,
        };
      } else {
        return {
          name: occupationPreference.occupation.name,
          id: slugifyCustomRoleName(occupationPreference.occupation.name),
          type: ROLE_OPTIONS_TYPE.CUSTOM,
        };
      }
    }) || null;

  return value;
};

const formatSkillsWantedFromLearnerProfile = (skillsWantedData?: LearnerProfileDesiredSkillsQuery) => {
  const value =
    skillsWantedData?.LearnerProfile?.me?.desiredSkills?.reduce<Skill[]>((acc, skill) => {
      if (skill.__typename === 'Skill_Skill') {
        return [
          ...acc,
          {
            id: skill.id,
            skillName: skill.skillName,
            type: SKILL_OPTIONS_TYPE.DEFAULT,
          },
        ];
      } else {
        return acc;
      }
    }, []) || null;

  return value;
};

const formatEducationLevelFromLearnerObject = (data?: LearnerObjectAttributesEducationProfileQuery) => {
  switch (data?.LearnerObject?.attributesByUserId?.educationProfile?.educationLevel) {
    case 'LESS_THAN_HIGH_SCHOOL_DIPLOMA':
      return EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_LESS_THAN_HIGH_SCHOOL_DIPLOMA;
    case 'HIGH_SCHOOL_DIPLOMA':
      return EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_HIGH_SCHOOL_DIPLOMA;
    case 'COLLEGE_NO_DEGREE':
      return EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_COLLEGE_NO_DEGREE;
    case 'ASSOCIATE_DEGREE':
      return EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_ASSOCIATE_DEGREE;
    case 'BACHELOR_DEGREE':
      return EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_BACHELOR_DEGREE;
    case 'MASTERS_DEGREE':
      return EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_MASTERS_DEGREE;
    case 'PROFESSIONAL_DEGREE':
      return EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_PROFESSIONAL_DEGREE;
    case 'DOCTORATE_DEGREE':
      return EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_DOCTORATE_DEGREE;
    default:
      return null;
  }
};

const formatLearningGoalFromLearnerObject = (learningGoalData?: LearnerObjectAttributesLearningGoalQuery) => {
  switch (learningGoalData?.LearnerObject?.attributesByUserId?.learningGoal) {
    case 'CHANGE_CAREER':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_CHANGE_CAREER;
    case 'IMPROVE_SKILLS':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS;
    case 'LAND_NEW_JOB':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_LAND_NEW_JOB;
    case 'PERSONAL_INTEREST':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_PERSONAL_INTEREST;
    default:
      return null;
  }
};

const formatCurrentOccupationFromLearnerObject = (
  currentOccupationData?: LearnerObjectAttributesCurrentOccupationQuery
): Record<string, string> | null => {
  const currentOccupation = currentOccupationData?.LearnerObject?.attributesByUserId?.currentOccupation;

  if (!currentOccupation || !currentOccupation.occupation) {
    return null;
  }

  if (currentOccupation.occupationInputType === 'DEFINED') {
    return { id: currentOccupation.occupation };
  } else if (currentOccupation.occupationInputType === 'USER_GENERATED') {
    return { name: currentOccupation.occupation };
  }

  return null;
};
const formatInterestedCareersFromLearnerObject = (
  interestedCareersData?: LearnerObjectAttributesInterestedCareersQuery
) => {
  const careers = interestedCareersData?.LearnerObject?.attributesByUserId?.interestedCareers?.careers;

  if (!careers || careers.length === 0) {
    return null;
  }

  return careers.map((career) => {
    if (career.__typename === 'LearnerObject_OccupationId') {
      return {
        id: career.id,
        name: '',
        type: ROLE_OPTIONS_TYPE.DEFAULT,
      };
    } else if (career.__typename === 'LearnerObject_UserGeneratedOccupation') {
      return {
        name: career.name,
        id: slugifyCustomRoleName(career.name),
        type: ROLE_OPTIONS_TYPE.CUSTOM,
      };
    } else {
      // Fallback for any other type
      return {
        id: '',
        name: '',
        type: ROLE_OPTIONS_TYPE.DEFAULT,
      };
    }
  });
};

const formatSkillsWantedFromLearnerObject = (
  skillsWantedData?: LearnerObjectAttributesSkillsWantedQuery
): Skill[] | null => {
  const skills = skillsWantedData?.LearnerObject?.attributesByUserId?.skillsWantedV2?.skills;

  if (!skills || skills.length === 0) {
    return null;
  }

  const value = skills.reduce<Skill[]>((acc, skill) => {
    if (skill.__typename === 'Skill_Skill') {
      return [
        ...acc,
        {
          id: skill.id,
          skillName: skill.skillName,
          type: SKILL_OPTIONS_TYPE.DEFAULT,
        },
      ];
    }
    // Filter out LearnerObject_UserGeneratedSkill - not supported in onboarding flow
    return acc;
  }, []);

  return value.length > 0 ? value : null;
};

export {
  formatEducationLevelFromLearnerObject,
  formatLearningGoalFromLearnerObject,
  formatSkillsWantedFromLearnerProfile,
  formatSkillsWantedFromLearnerObject,
  formatOccupationPreferencesFromLearnerProfile,
  formatInterestedCareersFromLearnerObject,
  formatCurrentOccupationFromLearnerObject,
};
